import { useState } from 'react'
import type { SalesBusinessDto } from '@lib/dto/sales-business.dto'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useCreateSalesBusiness, useVenueSalesBusiness } from '@/hooks/useSales'
import { BusinessPortal } from './BusinessPortal'

export const BUSINESS_STATUS: Record<SalesBusinessDto['status'], { label: string; tone: BadgeTone }> = {
  onboarding: { label: 'Being set up', tone: 'warning' },
  active: { label: 'Taking payments', tone: 'success' },
  restricted: { label: 'Needs more details', tone: 'danger' },
}

export const SET_UP_HINT = 'Verify the business to take payments. You’ll need its legal name, tax ID, an owner and a bank account.'

/**
 * Who a venue's payments go to (docs/sales.spec.md, "Stripe"): the company's
 * business, or the venue's own when it's a business of its own or has its own
 * bank account. Someone who sets up integrations at the venue can set its own
 * up here.
 */
export function VenueBusinessSection({ venueId, venueName }: { venueId: string; venueName: string }) {
  const query = useVenueSalesBusiness(venueId)
  const create = useCreateSalesBusiness()
  const [confirming, setConfirming] = useState(false)
  if (query.isPending) return <DetailSkeleton />
  if (query.isError) return <ErrorState message={requestErrorMessage(query.error)} onRetry={() => void query.refetch()} />
  const data = query.data
  if (!data.available) return <p className="m-0 text-sm text-ink-muted">Taking payments isn’t available yet.</p>

  const status = data.business === null ? null : BUSINESS_STATUS[data.business.status]
  return (
    <div className="space-y-4">
      {data.paidBy === 'none' ? (
        <Callout tone="neutral" title="Nobody is set up to be paid yet">
          An organization admin can set up the company’s business on the Getting paid page, and every venue is paid through it.
          {data.canSetUpOwn ? ' Or set up this venue as a business of its own.' : ''}
        </Callout>
      ) : (
        <p className="m-0 flex flex-wrap items-center gap-2 text-sm text-ink">
          {data.paidBy === 'venue' ? `${venueName} is paid as a business of its own.` : 'Paid through the company’s business.'}
          {status === null ? null : <Badge tone={status.tone}>{status.label}</Badge>}
        </p>
      )}

      {data.paidBy === 'venue' && data.business !== null && data.canSetUpOwn ? <BusinessPortal business={data.business} /> : null}

      {data.paidBy !== 'venue' && data.canSetUpOwn ? (
        <>
          <Button variant="outline" onClick={() => setConfirming(true)} disabled={create.isPending}>
            Pay this venue separately
          </Button>
          <ConfirmDialog
            open={confirming}
            onOpenChange={setConfirming}
            title={`Pay ${venueName} separately?`}
            description={`${SET_UP_HINT} Its payments will go to its own bank account instead of the company’s.`}
            confirmLabel="Set it up"
            busy={create.isPending}
            error={create.isError ? requestErrorMessage(create.error) : null}
            onConfirm={() => create.mutate(venueId, { onSuccess: () => setConfirming(false) })}
          />
        </>
      ) : null}
    </div>
  )
}
